/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
    bool, number, shape,
} from 'prop-types';
import { useMediaQuery } from '@material-ui/core';
import { useSelector } from 'react-redux';

import SkeletonStyles from './SkeletonStyles';
import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';

const useStyles = makeStyles((theme) => ({
    // Import Skeleton layout
    ...SkeletonStyles,

    root: {
        position: 'relative',
        maxWidth: '1400px',
        margin: '32px auto',
        [theme.breakpoints.down(1400)]: {
            margin: '32px 20px',
        },
        [theme.breakpoints.down(1024)]: {
            margin: '32px 10px',
        },
    },
    heading: {
        margin: '0 auto 30px auto',
        width: '25%',
        height: '40px',
        [theme.breakpoints.down(1024)]: {
            margin: '0 auto 40px auto',
        },
        [theme.breakpoints.down(600)]: {
            width: '50%',
            margin: '0 auto 20px auto',
        },
    },
    productContainer: (props) => ({
        width: '100%',
        display: 'flex',
        flexWrap: 'nowrap',
        overflowX: 'scroll',
    }),
    tabContainer: {
        display: 'flex',
        width: '70%',
        height: '45px',
        margin: '10px 0 25px',
        borderBottom: '3px solid #E7EDF1',
        [theme.breakpoints.down(600)]: {
            width: '100%',
        },
    },
    tabBox: {
        display: 'flex',
        width: '15%',
        padding: '10px 20px',
        [theme.breakpoints.down(600)]: {
            minWidth: '30%',
            overflowX: 'scroll',
        },
    },
    tabText: {
        // margin: '0px 16px',
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        background: 'linear-gradient(270deg, #E7EDF1, #F2F8FC, #E7EDF1)',
        backgroundSize: '1000% 1000%',
    },
    // Page Elements
    productGrid: (props) => ({
        '&--product-container': {
            width: '16.66666666%',
            padding: '0 20px 0px 0px',
            [theme.breakpoints.down(600)]: {
                minWidth: '40%',
                padding: '0 10px 0px 0px',
            },
        },
        '&--product-block': {
            clipPath: props?.showCircularSkeleton ? 'circle()' : '',
            paddingBottom: '106%',
        },
        '&--product-title': {
            width: '80%',
            height: '10px',
        },
        '&--product-price': {
            width: '50%',
            height: '10px',
        },
    }),

}));

const TabTrendingProductSkeleton = ({
    isTemplateProduct, nDisplayProducts, breakPoints, showCircularSkeleton,
}) => {
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const isMobile = useMediaQuery(`(max-width: ${breakPoints.mobile}px)`);
    const isDesktop = useMediaQuery(`(min-width: ${breakPoints.desktop}px)`);
    const isTablet = useMediaQuery(`(min-width: ${breakPoints.mobile}px) and (max-width: ${breakPoints.desktop}px)`);

    // Product to show according to conditions
    const onDesktop = nDisplayProducts.desktop;
    const onMobile = isTemplateProduct ? 1 : nDisplayProducts.mobile;

    // eslint-disable-next-line no-nested-ternary
    let productToShow = isMobile ? onMobile : (isDesktop ? onDesktop : nDisplayProducts.tablet);

    // SSR
    if (!isMobile && !isDesktop && !isTablet) {
        productToShow = ssrDeviceType === 'mobile' ? 2 : 5;
    }

    const classes = useStyles({ productToShow, showCircularSkeleton });

    return (
        <div data-testid="TabTrendingProductSkeleton" className={classes.root}>
            <div className={`${classes.heading} ${classes.textFW} ${classes.shimmer}`} />
            <div className={classes.tabContainer}>
                <div className={classes.tabBox}>
                    <div className={`${classes.tabText} ${classes.shimmer}`} />
                </div>
                <div className={classes.tabBox}>
                    <div className={`${classes.tabText} ${classes.shimmer}`} />
                </div>
                <div className={classes.tabBox}>
                    <div className={`${classes.tabText} ${classes.shimmer}`} />
                </div>
                <div className={classes.tabBox}>
                    <div className={`${classes.tabText} ${classes.shimmer}`} />
                </div>
                <div className={classes.tabBox}>
                    <div className={`${classes.tabText} ${classes.shimmer}`} />
                </div>
            </div>
            <div className={classes.productContainer}>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
                <div className={`${classes.productGrid}--product-container`}>
                    <div className={`${classes.productGrid}--product-block ${classes.textFW} ${classes.shimmer}`} />
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-title ${classes.textSM} ${classes.shimmer}`} />}
                    {!showCircularSkeleton && <div className={`${classes.productGrid}--product-price ${classes.textSM} ${classes.shimmer}`} />}
                </div>
            </div>
        </div>
    );
};

TabTrendingProductSkeleton.propTypes = {
    isTemplateProduct: bool,
    nDisplayProducts: shape({
        mobile: number,
        desktop: number,
        tablet: number,
    }),
    breakPoints: shape({
        mobile: number,
        desktop: number,
    }),
    showCircularSkeleton: bool,
};

TabTrendingProductSkeleton.defaultProps = {
    isTemplateProduct: false,
    nDisplayProducts: {
        mobile: 2,
        desktop: 5,
        tablet: 3,
    },
    breakPoints: {
        mobile: 600,
        desktop: 1024,
    },
    showCircularSkeleton: false,
};

export default TabTrendingProductSkeleton;
